import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatCardioDuration,
  formatDistance,
  extraSignGlyph,
  formatWeight,
  sessionSetGroups,
  type ExerciseResult,
  type LogSet,
  type WeightUnitLabels,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";
import { exerciseTrackLabel } from "../Logger/exerciseTrackLabel";
import { weightUnitLabels } from "../weightUnitLabels";

// A session read set by set, read-only (#25: the coach opens an athlete's
// session). One block per exercise in the order the athlete did them; each
// set reads load × reps, then its effort, a failed or AMRAP flag and the
// record mark. Health fields (calories, heart rate) are never read here.

export type SessionSetsListProps = {
  theme: UiTheme;
  sets: readonly LogSet[];
  /** The session's SessionResult rows, for the record mark; empty = none. */
  exerciseResults?: readonly ExerciseResult[];
  resolveName: (slug: string, fallback?: string) => string;
  unit: "kg" | "lb";
  locale: string;
};

type Tr = (k: string, opts?: Record<string, unknown>) => string;

/** What a set did: load × reps, or time and distance for the timed kinds. */
function setLoadText(
  set: LogSet,
  unit: "kg" | "lb",
  locale: string,
  labels: WeightUnitLabels,
  t: Tr,
): string {
  const w = (kg: number) => formatWeight(kg, unit, locale, labels);
  const reps = set.reps ?? 0;
  if (set.kind === "timed") return formatCardioDuration(set.duration ?? 0);
  if (set.kind === "cardio" || set.kind === "loaded-carry") {
    const parts: string[] = [];
    if (set.kind === "loaded-carry" && set.weight !== undefined)
      parts.push(w(set.weight));
    if (set.duration) parts.push(formatCardioDuration(set.duration));
    if (set.distanceM) parts.push(formatDistance(set.distanceM, locale));
    return parts.length > 0 ? parts.join(" · ") : "0:00";
  }
  if (set.dropset) {
    return set.dropset.steps
      .map((st) => (st.weight !== undefined ? `${w(st.weight)} × ${st.reps}` : `${st.reps}`))
      .join(" → ");
  }
  if (set.kind === "bodyweight") {
    const base = `${reps} ${t("workout.reps")}`;
    if (!set.extra) return base;
    return `${base} · ${extraSignGlyph(set.extra < 0)}${w(Math.abs(set.extra))}`;
  }
  if (set.weight !== undefined) {
    const sides = set.loadedSides === 2 ? "2× " : "";
    return `${sides}${w(set.weight)} × ${reps}`;
  }
  if (set.kind === "p1rm" && set.p1rm !== undefined) return `${set.p1rm}% × ${reps}`;
  return `${reps} ${t("workout.reps")}`;
}

/** The effort the athlete logged, in the scale they logged it in. */
const effortText = (set: LogSet, t: Tr): string | null => {
  if (set.rir !== undefined) return t("workout.rir", { n: set.rir });
  if (set.rpe !== undefined) return `${t("workout.rpe")} ${set.rpe}`;
  return null;
};

export function SessionSetsList({
  theme,
  sets,
  exerciseResults,
  resolveName,
  unit,
  locale,
}: SessionSetsListProps) {
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const labels = useMemo(() => weightUnitLabels(t), [t]);
  const groups = useMemo(
    () => sessionSetGroups(sets, exerciseResults),
    [sets, exerciseResults],
  );
  const recordLabel = t("mobile.summary.prBadge", {
    defaultValue: "personal record",
  });
  const failedLabel = t("mobile.logger.failBadge", { defaultValue: "Failed" });
  const amrapLabel = t("program.amrapBadge", { defaultValue: "AMRAP" });

  return (
    <View style={styles.card}>
      <Text style={styles.title} accessibilityRole="header">
        {t("coach.sessions.setsTitle", { defaultValue: "Sets" })}
      </Text>
      {groups.length === 0 ? (
        <Text style={styles.empty}>
          {t("coach.sessions.noSets", {
            defaultValue: "No sets logged in this session.",
          })}
        </Text>
      ) : null}
      {groups.map((g) => (
        <View key={g.key} style={styles.group}>
          <Text style={styles.exercise} accessibilityRole="header">
            {exerciseTrackLabel(
              resolveName(g.exerciseSlug, g.exerciseName),
              g.equipmentInstance,
              t,
            )}
          </Text>
          {g.rows.map(({ set, record }, i) => {
            const load = setLoadText(set, unit, locale, labels, t);
            const effort = effortText(set, t);
            const flags = [
              effort,
              set.fail ? failedLabel : null,
              set.amrap ? amrapLabel : null,
              record ? recordLabel : null,
            ].filter((x): x is string => x !== null);
            return (
              // One element for a screen reader: the number, the load, the flags.
              <View
                key={set.id}
                style={styles.row}
                accessible
                accessibilityLabel={t("coach.sessions.setA11y", {
                  n: i + 1,
                  summary: [load, ...flags].join(", "),
                  defaultValue: "Set {{n}}: {{summary}}",
                })}
              >
                <Text style={styles.index}>{i + 1}</Text>
                <View style={styles.body}>
                  <Text style={styles.load}>{load}</Text>
                  <View style={styles.flags}>
                    {effort ? <Text style={styles.flag}>{effort}</Text> : null}
                    {set.fail ? <Text style={styles.flag}>{failedLabel}</Text> : null}
                    {set.amrap ? <Text style={styles.flag}>{amrapLabel}</Text> : null}
                    {record ? (
                      <Icon
                        name="trophy"
                        size={theme.typography.sizes.base}
                        color={theme.palette.pr}
                        fill={theme.palette.pr}
                        label={recordLabel}
                      />
                    ) : null}
                  </View>
                </View>
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s4,
    },
    title: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      textTransform: "uppercase",
    },
    empty: { color: p.ink2, fontSize: ty.sizes.sm },
    group: { gap: s.s2 },
    exercise: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      paddingVertical: s.s2,
    },
    // The load grows to push the flags right while they fit on its line; at a
    // large text scale they wrap to the next line, starting under the load.
    body: {
      flex: 1,
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      columnGap: s.s3,
      rowGap: s.s1,
    },
    index: {
      minWidth: s.s5,
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    load: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
      flexGrow: 1,
      flexShrink: 1,
    },
    flags: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: s.s2,
      flexShrink: 1,
    },
    flag: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "600" },
  });
}
